import { delegate } from 'kerfjs';

type Point = { x: number; y: number };
type Bounds = Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>;

const clamp = (value: number): number => Math.max(0, Math.min(1, value));

export function imagePoint(clientX: number, clientY: number, bounds: Bounds): Point | null {
  if (bounds.width <= 0 || bounds.height <= 0) return null;
  return { x: clamp((clientX - bounds.left) / bounds.width), y: clamp((clientY - bounds.top) / bounds.height) };
}

export function arrowPoint(point: Point, key: string, shift: boolean): Point | null {
  const step = shift ? 0.05 : 0.01;
  switch (key) {
    case 'ArrowLeft':
      return { ...point, x: clamp(point.x - step) };
    case 'ArrowRight':
      return { ...point, x: clamp(point.x + step) };
    case 'ArrowUp':
      return { ...point, y: clamp(point.y - step) };
    case 'ArrowDown':
      return { ...point, y: clamp(point.y + step) };
    default:
      return null;
  }
}

/** Wire editable gallery pins without updating reactive state until a drag ends. */
export function wireGalleryAnnotationDrag(
  root: HTMLElement,
  onMove: (annotationId: string, point: Point) => void,
): () => void {
  let drag:
    | {
        marker: HTMLElement;
        pointerId: number;
        annotationId: string;
        startX: number;
        startY: number;
        original: Point;
      }
    | undefined;
  const selector = '[data-document-annotation-marker]';
  const pointOf = (element: HTMLElement): Point | null => {
    const rawX = element.getAttribute('data-annotation-x');
    const rawY = element.getAttribute('data-annotation-y');
    if (rawX === null || rawY === null) return null;
    const x = Number(rawX);
    const y = Number(rawY);
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
  };
  const imageOf = (element: HTMLElement): HTMLImageElement | null =>
    element.closest('.document-preview-image-wrap')?.querySelector('.document-preview-image') ?? null;
  const reset = (): void => {
    if (drag !== undefined) {
      drag.marker.style.left = `${String(drag.original.x * 100)}%`;
      drag.marker.style.top = `${String(drag.original.y * 100)}%`;
    }
    drag = undefined;
  };
  const disposers = [
    delegate(root, 'pointerdown', selector, (event, element) => {
      const pointer = event as PointerEvent;
      const marker = element as HTMLElement;
      const annotationId = element.getAttribute('data-item-id');
      const original = pointOf(marker);
      if (drag !== undefined && !drag.marker.isConnected) drag = undefined;
      if (
        drag !== undefined ||
        !pointer.isPrimary ||
        pointer.button !== 0 ||
        annotationId === null ||
        original === null
      )
        return;
      if (imageOf(marker) === null) return;
      drag = {
        marker,
        pointerId: pointer.pointerId,
        annotationId,
        startX: pointer.clientX,
        startY: pointer.clientY,
        original,
      };
      marker.setPointerCapture(pointer.pointerId);
      pointer.preventDefault();
    }),
    delegate(root, 'pointermove', selector, (event, element) => {
      const pointer = event as PointerEvent;
      if (drag?.marker !== element || drag.pointerId !== pointer.pointerId) return;
      const marker = element as HTMLElement;
      const image = imageOf(marker);
      const point = image === null ? null : imagePoint(pointer.clientX, pointer.clientY, image.getBoundingClientRect());
      if (point === null) return;
      marker.style.left = `${String(point.x * 100)}%`;
      marker.style.top = `${String(point.y * 100)}%`;
    }),
    delegate(root, 'pointerup', selector, (event, element) => {
      const pointer = event as PointerEvent;
      if (drag?.marker !== element || drag.pointerId !== pointer.pointerId) return;
      const moved = Math.hypot(pointer.clientX - drag.startX, pointer.clientY - drag.startY) >= 3;
      const image = imageOf(element as HTMLElement);
      const point = image === null ? null : imagePoint(pointer.clientX, pointer.clientY, image.getBoundingClientRect());
      const annotationId = drag.annotationId;
      reset();
      if (moved && point !== null) onMove(annotationId, point);
    }),
    delegate(root, 'pointercancel', selector, (event, element) => {
      const pointer = event as PointerEvent;
      if (drag?.marker === element && drag.pointerId === pointer.pointerId) reset();
    }),
    delegate(root, 'keydown', selector, (event, element) => {
      const keyboard = event as KeyboardEvent;
      const annotationId = element.getAttribute('data-item-id');
      const current = pointOf(element as HTMLElement);
      if (annotationId === null || current === null) return;
      const next = arrowPoint(current, keyboard.key, keyboard.shiftKey);
      if (next === null) return;
      keyboard.preventDefault();
      if (next.x !== current.x || next.y !== current.y) onMove(annotationId, next);
    }),
  ];
  return () => {
    reset();
    for (const dispose of disposers) dispose();
  };
}
